import React, { useRef, useState } from 'react';
import {
  ButtonsContainer,
  TitleAndSubtitle,
  ContentButtonsWrapper,
} from '../../../layout/layout-container.styled';
import { NavButton } from '../../../nav-button/nav-button';
import { Text, font } from '../../../../style-utils/text';
import { TextEditor, TextEditorHandle } from '../../../styled-quill/text-editor';
import { IqCleanupHint } from '../../../iq/iq-cleanup-hint';
import { useIqEntitlement } from '../../../hooks/use-iq-entitlement';
import { VisitorLogState } from '../../../hooks/use-visitor-log-handler';
import { DefaultComponentProps } from '../../../app-router/component-props';
import { DailyLogState } from '../../../hooks/use-daily-log-handler';
import { createItem } from '../../../requests/requests';
import toast from '../../../../utils/toast';
import styled from '@emotion/styled';
import {
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
} from '../../../../style-utils/theme';

/* Calms the react-quill "snow" surface: hairline borders, soft radius, muted
 * toolbar, and an animated iris focus ring — instead of the stock grey chrome. */
const NotesSurface = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  & .quill {
    width: 100%;
    max-width: 600px;
    margin-bottom: ${spacing.md};
  }

  & .ql-toolbar.ql-snow {
    border-color: ${palette.line};
    border-top-left-radius: ${radius.xs};
    border-top-right-radius: ${radius.xs};
    background: ${palette.surfaceMuted};
    transition: ${transition.colors};
  }

  & .ql-container.ql-snow {
    border-color: ${palette.line};
    border-bottom-left-radius: ${radius.xs};
    border-bottom-right-radius: ${radius.xs};
    background: ${palette.surface};
    font-family: ${typography.ui};
    font-size: ${typography.size.base}px;
    color: ${palette.ink};
    transition: ${transition.colors};
  }

  & .ql-editor {
    padding: ${spacing.sm};
    line-height: ${typography.leading.normal};
  }

  & .ql-editor.ql-blank::before {
    color: ${palette.slateLight};
    font-style: normal;
  }

  &:focus-within .ql-toolbar.ql-snow,
  &:focus-within .ql-container.ql-snow {
    border-color: ${palette.lagoon};
  }

  &:focus-within .ql-container.ql-snow {
    box-shadow: ${shadow.focus};
  }
`;

export const Notes: React.FC<DefaultComponentProps> = ({
  navigate,
  changeHandler,
  state,
  options,
  prevPath,
  nextPath,
  path,
}) => {
  const { notes } = state as VisitorLogState;
  const { additionalNotes, description } = state as DailyLogState;
  const isLog = path?.includes('/daily-log') || false;
  const isLastPath = '/main-menu' === nextPath;

  const getComponentType = (options: string): string => {
    switch (options) {
      case 'DESCRIPTION':
        return 'description';
      case 'ADDITIONAL_NOTES':
        return 'additionalNotes';
      default:
        return 'notes';
    }
  };

  const getTitleFromComponentType = (options: string) => {
    switch (options) {
      case 'DESCRIPTION':
        return 'Description';
      case 'ADDITIONAL_NOTES':
        return 'Additional Notes';
      default:
        return 'Notes';
    }
  };

  const getStateFromType = (options: string) => {
    switch (options) {
      case 'DESCRIPTION':
        return description;
      case 'ADDITIONAL_NOTES':
        return additionalNotes;
      default:
        return notes;
    }
  };

  const type = getComponentType(options?.[0] || '');
  const title = getTitleFromComponentType(options?.[0] || '');
  const stateFromType = getStateFromType(options?.[0] || '');
  const [loading, setLoading] = useState(false);
  const submissionRef = useRef(false);
  const editorRef = useRef<TextEditorHandle>(null);
  // Polarity is deliberate: not-loaded reads as OFF, so an aurora surface is
  // never shown to someone /api/iq/* would 403.
  const { state: iqState } = useIqEntitlement();

  const submitLog = async () => {
    if (submissionRef.current) {
      return;
    }

    submissionRef.current = true;
    setLoading(true);

    try {
      await createItem(isLog ? '/daily-log' : '/visitor-log', state);
      toast.success(isLog ? 'Building activity saved' : 'Visitor log saved');
      navigate(nextPath, { replace: false });
      /* THE GUARD IS NOT RELEASED ON SUCCESS, AND THAT IS THE POINT.
         It used to be released in a `finally`, which runs before React has
         processed the navigation and unmounted this step. A second press landing
         in that gap — the ordinary "did that work?" double-tap on a slow
         connection — passed the guard and wrote a SECOND log entry: two
         identical arrivals, seconds apart, from one visitor at the door. The
         entry is already saved and this component is on its way out, so there is
         nothing here left to press; the only thing releasing the flag could
         achieve is letting it be pressed again.
         `loading` stays true for the same reason: the button must not go back to
         reading "Finish" on a step that has already finished. */

      return;
    } catch {
      toast.error('We could not save this log. Please try again.');
      // ONLY the failure path re-arms. Nothing was written, so the desk has to
      // be able to try again — that is what "Please try again" promises.
      submissionRef.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      <TitleAndSubtitle direction="column">
        <Text as="h1" fontSize={font.size.xl} fontWeight={700} mt={0} mb={8}>
          {title}
        </Text>
        <Text color={font.color.gray} fontSize={font.size.xs} mt={0} mb={0}>
          Enter {title}{' '}
        </Text>
      </TitleAndSubtitle>
      <ContentButtonsWrapper>
        <NotesSurface>
          <TextEditor
            ref={editorRef}
            changeHandlerFor={type}
            defaultValue={stateFromType}
            changeHandler={changeHandler}
          />
          <IqCleanupHint
            value={stateFromType}
            field={type}
            enabled={iqState?.enabled === true}
            onApply={text => editorRef.current?.applyText(text)}
          />
        </NotesSurface>
      </ContentButtonsWrapper>
      <ButtonsContainer>
        <NavButton
          disabled={false}
          direction="back"
          textColor={palette.slate}
          background="transparent"
          text={'Back'}
          onClick={() => navigate(prevPath, { replace: false })}
        />
        <NavButton
          disabled={false}
          direction="forward"
          text={isLastPath ? 'Finish' : 'Next'}
          loading={loading}
          onClick={
            isLastPath
              ? submitLog
              : () => navigate(nextPath, { replace: false })
          }
        />
      </ButtonsContainer>
    </>
  );
};
